import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { imageProps, cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * The image banner.
 *
 * Edge to edge, directly under the header: whatever artwork an operator has
 * uploaded in the admin panel, always filling the full width with no bars or
 * fill colour beside it. The box is reserved at the first banner's own
 * proportions (its phone artwork's on phones) before a byte arrives, so on a
 * phone the banner is shown exactly as drawn, and a later banner cut to a
 * different shape fills the same box rather than nudging the page under it on
 * every turn.
 *
 * On a tablet or larger the box is also capped at the screen height left under
 * the announcement bar and header, so the banner fits on one screen at any
 * resolution. Where the cap bites, the banner still fills the full width and
 * the overflow is trimmed evenly from its top and bottom — on a 1920×940
 * browser window, about 4% off each edge of a 2.1:1 banner. Keep lettering and
 * products away from the top and bottom tenth of the artwork and nothing that
 * matters is ever trimmed. Phones keep the full-width box: a portrait screen
 * is taller than any landscape banner.
 *
 * Two or more banners cross-fade every five seconds. The rotation holds while a
 * pointer rests on it, while keyboard focus is inside, while the tab is in the
 * background and whenever the visitor presses pause; under reduced motion it
 * never starts. One banner is just an image — no controls, no timer.
 */

const INTERVAL = 5000;
const SWIPE_THRESHOLD = 40;
const FALLBACK_RATIO = '1920 / 700';

// Matches Tailwind's `md` breakpoint, so the reserved box and the artwork the
// browser picks switch at the same pixel.
const PHONE_QUERY = '(max-width: 767px)';

/** Banners the storefront can actually draw: switched on, with an image. */
export const showableSlides = (slides) =>
  (Array.isArray(slides) ? slides : []).filter((slide) => slide && slide.enabled !== false && slide.image?.url);

const ratioOf = (image, fallback = FALLBACK_RATIO) =>
  image?.width > 0 && image?.height > 0 ? `${image.width} / ${image.height}` : fallback;

/**
 * How far below the top of the page the banner starts — the announcement bar
 * and the header above it. Measured rather than assumed, because the bar can
 * be switched off, wraps to two lines on a narrow window, and arrives with the
 * settings after the first paint. Re-measured whenever the page above or the
 * window changes size.
 */
const useOffsetTop = (ref) => {
  const [top, setTop] = useState(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => setTop(Math.round(element.getBoundingClientRect().top + window.scrollY));
    measure();

    const observer = new ResizeObserver(measure);
    for (let node = element; node && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement?.children ?? []) {
        if (sibling !== node && sibling.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) {
          observer.observe(sibling);
        }
      }
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref]);

  return top;
};

const lqipOf = (image) => (image?.lqip ? `url("${image.lqip}")` : 'none');

// Only two kinds of link are accepted by the admin: a path on this site, which
// stays in the router, and a full https address, which opens beside it.
const linkKind = (link) => {
  const value = String(link ?? '').trim();
  if (value.startsWith('/') && !value.startsWith('//')) return 'internal';
  if (/^https:\/\//i.test(value)) return 'external';
  return null;
};

export const HeroSlider = ({ slides }) => {
  const shown = showableSlides(slides);
  const count = shown.length;
  const multiple = count > 1;
  const reduced = useReducedMotion();

  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  const swipeStart = useRef(null);
  const swiped = useRef(false);
  const boxRef = useRef(null);
  const offsetTop = useOffsetTop(boxRef);

  // The list can shrink under a mounted slider when settings refetch.
  const active = count ? index % count : 0;
  const canAutoplay = multiple && !reduced;
  const playing = canAutoplay && !paused && !hovered && !focused && !hidden;

  useEffect(() => {
    const update = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  // A fresh timer per banner, so a manual move always earns a full five seconds.
  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setTimeout(() => setIndex((current) => (current + 1) % count), INTERVAL);
    return () => window.clearTimeout(timer);
  }, [playing, index, count]);

  if (!count) return null;

  const goTo = (next) => setIndex(((next % count) + count) % count);

  const first = shown[0];
  const boxStyle = {
    '--hero-ratio': ratioOf(first.image),
    '--hero-ratio-phone': first.mobileImage?.url ? ratioOf(first.mobileImage, ratioOf(first.image)) : ratioOf(first.image),
    // Until measured, the header's usual height, so the first paint is already close.
    '--hero-top': offsetTop === null ? '7rem' : `${offsetTop}px`,
  };
  const boxClass =
    'relative w-full overflow-hidden bg-cream aspect-(--hero-ratio-phone) md:aspect-(--hero-ratio) md:max-h-[calc(100svh-var(--hero-top))]';

  if (!multiple) {
    return (
      <section ref={boxRef} aria-label="Featured" className={boxClass} style={boxStyle}>
        <Slide slide={first} eager />
      </section>
    );
  }

  const onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(active - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(active + 1);
    }
  };

  // Only keyboard focus holds the rotation. A mouse click on an arrow also
  // focuses it, and that should not freeze the banner after the pointer leaves.
  const onFocus = (event) => setFocused(event.target.matches(':focus-visible'));
  const onBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
  };

  const onPointerDown = (event) => {
    swiped.current = false;
    swipeStart.current = event.pointerType === 'mouse' ? null : { x: event.clientX, y: event.clientY };
  };
  const onPointerUp = (event) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    swiped.current = true;
    goTo(active + (dx < 0 ? 1 : -1));
  };
  // A swipe that ends on a linked banner must not also follow the link.
  const onClickCapture = (event) => {
    if (!swiped.current) return;
    swiped.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <section
      ref={boxRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      onPointerEnter={(event) => event.pointerType === 'mouse' && setHovered(true)}
      onPointerLeave={(event) => event.pointerType === 'mouse' && setHovered(false)}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (swipeStart.current = null)}
      onClickCapture={onClickCapture}
      className={cn(
        boxClass,
        'group touch-pan-y select-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand',
      )}
      style={boxStyle}
    >
      <div aria-live={playing ? 'off' : 'polite'} className="absolute inset-0">
        {shown.map((slide, slideIndex) => {
          const isActive = slideIndex === active;
          return (
            <div
              key={slide.id ?? slideIndex}
              role="group"
              aria-roledescription="slide"
              aria-label={`${slideIndex + 1} of ${count}`}
              aria-hidden={!isActive || undefined}
              inert={!isActive}
              // The incoming banner fades in on top; the outgoing one holds
              // until it is covered, so the fade never dips through to the
              // background.
              className={cn(
                'absolute inset-0 transition-opacity duration-600 ease-out motion-reduce:transition-none motion-reduce:delay-0',
                isActive ? 'z-10 opacity-100' : 'pointer-events-none z-0 opacity-0 delay-600',
              )}
            >
              <Slide slide={slide} eager={slideIndex === 0} />
            </div>
          );
        })}
      </div>

      <ArrowButton direction={-1} label="Previous banner" onClick={() => goTo(active - 1)} />
      <ArrowButton direction={1} label="Next banner" onClick={() => goTo(active + 1)} />

      <div className="absolute inset-x-0 bottom-1.5 z-20 flex justify-center sm:bottom-3">
        <div className="flex items-center">
          {shown.map((slide, slideIndex) => (
            <button
              key={slide.id ?? slideIndex}
              type="button"
              onClick={() => goTo(slideIndex)}
              aria-label={`Show banner ${slideIndex + 1}`}
              aria-current={slideIndex === active || undefined}
              className="group/dot grid h-6 place-items-center px-1 focus-visible:outline-2 focus-visible:outline-white"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'block h-2 rounded-full shadow-[0_0_0_1px_rgba(0,0,0,0.25)] transition-all duration-300 motion-reduce:transition-none',
                  slideIndex === active ? 'w-6 bg-brand' : 'w-2 bg-white/75 group-hover/dot:bg-white',
                )}
              />
            </button>
          ))}
        </div>
      </div>

      {canAutoplay && (
        <button
          type="button"
          onClick={() => setPaused((value) => !value)}
          aria-label={paused ? 'Play banner slideshow' : 'Pause banner slideshow'}
          className="absolute right-2 bottom-1.5 z-20 grid size-6 place-items-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:right-4 sm:bottom-3"
        >
          <svg viewBox="0 0 16 16" className="size-3" fill="currentColor" aria-hidden="true">
            {paused ? <path d="M5 3.2v9.6L12.6 8z" /> : <path d="M4.5 3h2.4v10H4.5zm4.6 0h2.4v10H9.1z" />}
          </svg>
        </button>
      )}
    </section>
  );
};

const Slide = ({ slide, eager }) => {
  const { image, mobileImage } = slide;
  const phone = mobileImage?.url ? mobileImage : null;
  const alt = slide.alt || image.alt || '';
  const kind = linkKind(slide.link);

  const picture = (
    <picture className="block size-full">
      {phone && (
        <source
          media={PHONE_QUERY}
          srcSet={phone.srcset || phone.url}
          sizes={phone.srcset ? '100vw' : undefined}
          width={phone.width || undefined}
          height={phone.height || undefined}
        />
      )}
      <img
        {...imageProps(image, { sizes: '100vw', eager })}
        alt={alt}
        draggable={false}
        className="size-full object-cover object-center"
      />
    </picture>
  );

  const linkClass =
    'block size-full focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white';

  return (
    <div
      className="size-full bg-cover bg-center bg-no-repeat bg-(image:--slide-lqip-phone) md:bg-(image:--slide-lqip)"
      style={{ '--slide-lqip': lqipOf(image), '--slide-lqip-phone': lqipOf(phone ?? image) }}
    >
      {kind === 'internal' ? (
        <Link to={slide.link.trim()} className={linkClass}>
          {picture}
        </Link>
      ) : kind === 'external' ? (
        <a href={slide.link.trim()} target="_blank" rel="noopener noreferrer" className={linkClass}>
          {picture}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      ) : (
        picture
      )}
    </div>
  );
};

const ArrowButton = ({ direction, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={cn(
      'absolute top-1/2 z-20 grid size-9 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-[opacity,background-color] duration-200 hover:bg-black/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:size-11',
      // Hidden until the banner is hovered on a mouse; always there on touch.
      'opacity-100 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
      direction === -1 ? 'left-2 sm:left-5' : 'right-2 sm:right-5',
    )}
  >
    <svg viewBox="0 0 16 16" className={cn('size-4 sm:size-5', direction === -1 && 'rotate-180')} fill="currentColor" aria-hidden="true">
      <path d="M6.2 3.2 5.14 4.26 8.88 8l-3.74 3.74L6.2 12.8 11 8z" />
    </svg>
  </button>
);

export default HeroSlider;
